import React, { useState } from 'react'
import cn from 'classnames'
import { useTranslation } from 'lib/i18n'

import Emoji, { EmojiType } from 'components/UI/Emoji/Emoji'
import VideoDuration from 'components/VideoDuration/VideoDuration'
import VideoPlayerWithControls from 'components/VideoPlayerWithControls/VideoPlayerWithControls'

import BookOccasionButton from '../BookOccasionButton/BookOccasionButton'
import { ProductChildrenProps, VideoCardProps } from '../type'
import VideoCardProfileReview from '../VideoCardProfileReview/VideoCardProfileReview'

import styles from './VideoCard.module.css'

const VideoCard: React.FC<VideoCardProps> = ({
  request,
  typicallyRespondsInDays,
  loading = false,
  productsWithExpressDelivery = [],
  profileSlug = '',
  hideButton = false,
}) => {
  const { t } = useTranslation()
  const [videoPlaying, setVideoPlaying] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [isVideoEnded, setIsVideoEnded] = useState(false)

  if (loading) return <div>Loading..</div>
  if (!request && !loading) return <div>Error..</div>

  const { id, video, review, requestOccasion } = request
  const { url, thumbnailUrl, duration } = video

  let expressDeliveryText = ''
  if (productsWithExpressDelivery.length !== 0) {
    const has1hDelivery = (products: ProductChildrenProps[]) =>
      products.find(
        (expressProduct) =>
          expressProduct?.product?.slug === '1h-express-delivery',
      )

    const has24hDelivery = (products: ProductChildrenProps[]) =>
      products.find((expressProduct) =>
        [
          'video-request-express-delivery',
          'video-request-business-express-delivery',
        ].includes(expressProduct?.product?.slug),
      )

    if (has1hDelivery(productsWithExpressDelivery)) {
      expressDeliveryText = t('expressDelivery2.1h', {
        defaultValue: '1h Available',
      })
    } else if (has24hDelivery(productsWithExpressDelivery)) {
      expressDeliveryText = t('expressDelivery2.24h', {
        defaultValue: '24h Available',
      })
    }
  }

  const text = t('playAgain', 'Play again')

  return (
    <div className={styles.card} data-cy="videoRequestCard">
      <div
        className={cn(styles.playerWrapper, {
          [styles.videoEndPlayAgainButton]: isVideoEnded,
        })}
      >
        <VideoPlayerWithControls
          src={url}
          poster={thumbnailUrl}
          onPlay={() => setVideoPlaying(true)}
          onPause={() => setVideoPlaying(false)}
          roundedCorners
          gradientBottom
          showVolumeControl
          unmuteOnPlay={false}
          showPlayButton={!!url && !expanded}
          customPlayIconStyles={styles.desktopPlaybutton}
          onVideoEnd={() => setIsVideoEnded(true)}
          isVideoEnded={isVideoEnded}
          text={text}
        />
        {!videoPlaying && url && isVideoEnded && !hideButton && (
          <BookOccasionButton
            profileSlug={profileSlug}
            occasionId={requestOccasion?.id}
          />
        )}
      </div>

      <div className={styles.aspect} />

      {!videoPlaying && (
        <>
          {!expanded && (
            <div
              className={cn(styles.occasionAndDuration, {
                [styles.hidden]: isVideoEnded,
              })}
            >
              {requestOccasion && (
                <div
                  className={styles.occasion}
                  style={{
                    backgroundColor:
                      requestOccasion?.color || 'hsla(0, 0%, 0%, 0.5)',
                  }}
                >
                  <Emoji
                    emoji={requestOccasion.emoji as EmojiType}
                    className={styles.emoji}
                  />
                  {requestOccasion.title}
                </div>
              )}

              {duration && (
                <div className={styles.duration}>
                  <VideoDuration duration={duration} />
                </div>
              )}
            </div>
          )}

          <div className={styles.background} />

          {id === 'talent-video' && (
            <div className={styles.deliveryText} style={{ textAlign: 'start' }}>
              <div className={styles.deliveryTextLabel}>
                {t('VideoCard.response', 'Responds within')}
              </div>
              <div>
                <span className={styles.deliveryTextDays}>
                  {t('VideoCard.responseDays', {
                    defaultValue: '{{count}} day',
                    defaultValue_plural: '{{count}} days',
                    count: typicallyRespondsInDays,
                  })}
                </span>

                {expressDeliveryText && (
                  <>
                    <span className={styles.deliveryTextDays}> / </span>
                    <span className={styles.deliveryTextExpress}>
                      {expressDeliveryText}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {review && (
            <main
              className={cn(styles.content, {
                [styles.contentExpanded]: expanded,
                [styles.hidden]: isVideoEnded,
              })}
            >
              <div
                className={cn(styles.reviewButton, {
                  [styles.reviewButtonExpanded]: expanded,
                })}
                onClick={() => setExpanded(!expanded)}
                tabIndex={0}
                role="button"
              >
                <VideoCardProfileReview review={review} expanded={expanded} />
              </div>
            </main>
          )}
        </>
      )}
    </div>
  )
}

export default VideoCard
